import {
  ALERT_CLOSED_STATUSES,
  ALERT_CRITICALITIES,
  ALERT_CRITICALITY_LABELS,
  ALERT_JUSTIFICATION_MAX,
  ALERT_OPEN_STATUSES,
  ALERT_STATUSES,
  ALERT_STATUS_LABELS,
  ALERT_TYPES,
  ALERT_TYPE_LABELS,
  alertFilterOptionsSchema,
  alertListSchema,
  alertOpenCountSchema,
  alertSchema,
  alertStatusInputSchema,
  formatDate,
  formatDateTime,
  type Alert,
  type AlertCriticalityName,
  type AlertStatusName,
  type AlertTypeName,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, FolderKanban, Lock } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ReportDeviationButton } from '@/features/alerts/ReportDeviation';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const PAGE_SIZE = 20;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CRITICALITY_LIGHT: Record<AlertCriticalityName, TrafficLight> = {
  LOW: 'blue',
  MEDIUM: 'yellow',
  HIGH: 'red',
  CRITICAL: 'red',
};
const STATUS_LIGHT: Record<AlertStatusName, TrafficLight> = {
  OPEN: 'yellow',
  IN_PROGRESS: 'blue',
  RESOLVED: 'green',
  DISMISSED: 'gray',
};
const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const periodText = (a: Pick<Alert, 'periodStart' | 'periodEnd'>) =>
  a.periodStart === a.periodEnd
    ? formatDate(a.periodStart)
    : `${formatDate(a.periodStart)} a ${formatDate(a.periodEnd)}`;

/**
 * Gestão › Alertas (T-111, RF-40; DEC-044, DEC-046): os desvios do projeto com filtros, as ações do ciclo
 * (assumir, resolver, descartar), gerar plano de ação e abrir o assunto; "Informar desvio" (DEC-045).
 */
export function CentralAlertasPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Alertas"
        description="Desvios dos indicadores, objetivos e ações do projeto: quem trata, o que foi decidido e o plano que nasceu deles."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os alertas pertencem a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: os alertas ficam disponíveis só para consulta.
            </p>
          ) : null}
          <AlertsSection
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </div>
      )}
    </PageContainer>
  );
}

type Filters = {
  type: AlertTypeName | '';
  state: 'open' | 'closed' | '';
  status: AlertStatusName | '';
  criticality: AlertCriticalityName | '';
  perspectiveId: string;
  departmentId: string;
  responsibleId: string;
  kpiId: string;
  businessUnitId: string;
  periodFrom: string;
  periodTo: string;
};
const EMPTY_FILTERS: Filters = {
  type: '',
  state: 'open',
  status: '',
  criticality: '',
  perspectiveId: '',
  departmentId: '',
  responsibleId: '',
  kpiId: '',
  businessUnitId: '',
  periodFrom: '',
  periodTo: '',
};

function AlertsSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/alerts`;
  const [params, setParams] = useSearchParams();
  // ?tipo=<tipo> abre já filtrado (link da Visão Geral — T-140b).
  const [filters, setFilters] = useState<Filters>(() => {
    const type = params.get('tipo');
    return (ALERT_TYPES as readonly string[]).includes(type ?? '')
      ? { ...EMPTY_FILTERS, type: type as AlertTypeName }
      : EMPTY_FILTERS;
  });
  const [page, setPage] = useState(1);
  const [closing, setClosing] = useState<{ alert: Alert; status: 'RESOLVED' | 'DISMISSED' } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof Filters) => (value: string) => {
    setFilters((f) => {
      const next = { ...f, [key]: value };
      // Situação e status andam juntos: status encerrado com "abertos" (ou o contrário) não traria nada.
      const closedStatus = (s: string) => (ALERT_CLOSED_STATUSES as readonly string[]).includes(s);
      if (key === 'status' && value) {
        if (next.state === 'open' && closedStatus(value)) next.state = '';
        if (next.state === 'closed' && !closedStatus(value)) next.state = '';
      }
      if (key === 'state' && next.status) {
        if (
          (value === 'open' && closedStatus(next.status)) ||
          (value === 'closed' && !closedStatus(next.status))
        ) {
          next.status = '';
        }
      }
      return next;
    });
    setPage(1);
  };

  const options = useQuery({
    queryKey: ['alerts', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: alertFilterOptionsSchema, signal }),
  });
  const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  for (const [key, value] of Object.entries(filters)) if (value) query.set(key, value);
  const list = useQuery({
    queryKey: ['alerts', projectId, 'list', query.toString()],
    queryFn: ({ signal }) =>
      client.request(`${base}?${query.toString()}`, { schema: alertListSchema, signal }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['alerts'] });
  // Link do aviso da central de notificações (T-113b): ?alerta= mostra o alerta em destaque, com as ações.
  const [linkedId, setLinkedId] = useState(() => {
    const raw = params.get('alerta') ?? '';
    return UUID.test(raw) ? raw : '';
  });
  useEffect(() => {
    if (!params.has('alerta')) return;
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('alerta');
        return out;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const linked = useQuery({
    queryKey: ['alerts', projectId, 'detail', linkedId],
    queryFn: ({ signal }) => client.request(`${base}/${linkedId}`, { schema: alertSchema, signal }),
    enabled: Boolean(linkedId),
    meta: { handlesError: true },
  });
  const openCount = useQuery({
    queryKey: ['alerts', 'open-count', organizationId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/alerts/open-count`, {
        schema: alertOpenCountSchema,
        signal,
      }),
    meta: { handlesError: true },
  });
  const elsewhere = (openCount.data?.projects ?? [])
    .filter((p) => p.projectId !== projectId)
    .reduce((n, p) => n + p.open, 0);
  // Ao encerrar o último alerta de uma página, a página deixa de existir: volta para a última que existe.
  const totalPages = list.data?.totalPages;
  useEffect(() => {
    if (totalPages === undefined) return;
    const last = Math.max(1, totalPages);
    if (page > last) setPage(last);
  }, [page, totalPages]);
  // Alguém (ou o motor) mudou o alerta antes: a recusa aparece e a lista se atualiza.
  const onConflict = (err: unknown) => {
    if (err instanceof ApiError && err.status === 409) void refresh();
  };

  const take = useMutation({
    meta: { handlesError: true },
    mutationFn: (alert: Alert) =>
      client.request(`${base}/${alert.id}/status`, {
        method: 'POST',
        body: { status: 'IN_PROGRESS' },
        schema: alertSchema,
      }),
    onSuccess: async (a) => {
      toast.success('Alerta em tratamento', { description: a.title });
      await refresh();
    },
    onError: (err) => {
      toast.error('Não foi possível assumir o alerta', { description: errorMessage(err) });
      onConflict(err);
    },
  });

  const filtered = Object.entries(filters).some(([k, v]) => v !== EMPTY_FILTERS[k as keyof Filters]);
  const ids = {
    type: useId(),
    state: useId(),
    status: useId(),
    criticality: useId(),
    perspective: useId(),
    department: useId(),
    responsible: useId(),
    kpi: useId(),
    unit: useId(),
    from: useId(),
    to: useId(),
  };
  const opts = options.data;

  return (
    <section aria-label="Central de alertas" className="flex flex-col gap-4">
      {linkedId ? (
        <section
          aria-label="Alerta do aviso"
          className="flex flex-col gap-3 rounded-lg border border-border-strong bg-brand-soft/40 p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold">Alerta do aviso</h2>
            <Button variant="ghost" size="sm" onClick={() => setLinkedId('')}>
              Fechar
            </Button>
          </div>
          {linked.isPending ? (
            <LoadingState rows={1} label="Carregando o alerta" />
          ) : linked.isError ? (
            <ErrorState
              title="Não foi possível abrir o alerta"
              error={linked.error}
              onRetry={() => void linked.refetch()}
              retrying={linked.isFetching}
            />
          ) : (
            <ul aria-label="Alerta do aviso" className="flex flex-col">
              <AlertCard
                alert={linked.data}
                organizationId={organizationId}
                canTreat={can('alertas', 'edit')}
                canPlan={can('planos-acao', 'create')}
                busy={take.isPending}
                onTake={() => take.mutate(linked.data)}
                onClose={(status) => setClosing({ alert: linked.data, status })}
              />
            </ul>
          )}
        </section>
      ) : null}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-bold">Filtros</h2>
          <div className="flex flex-wrap gap-2">
            {filtered ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(EMPTY_FILTERS);
                  setPage(1);
                }}
              >
                Limpar filtros
              </Button>
            ) : null}
            {can('alertas', 'create') ? (
              <ReportDeviationButton organizationId={organizationId} projectId={projectId} />
            ) : null}
          </div>
        </div>
        {options.isError ? (
          <ErrorState
            error={options.error}
            onRetry={() => void options.refetch()}
            retrying={options.isFetching}
          />
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Filter id={ids.state} label="Situação" value={filters.state} onChange={set('state')}>
            <option value="open">Abertos (aberto ou em tratamento)</option>
            <option value="closed">Encerrados</option>
            <option value="">Todos</option>
          </Filter>
          <Filter id={ids.status} label="Status" value={filters.status} onChange={set('status')}>
            <option value="">Todos os status</option>
            {ALERT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ALERT_STATUS_LABELS[s]}
              </option>
            ))}
          </Filter>
          <Filter id={ids.type} label="Tipo" value={filters.type} onChange={set('type')}>
            <option value="">Todos os tipos</option>
            {ALERT_TYPES.map((t) => (
              <option key={t} value={t}>
                {ALERT_TYPE_LABELS[t]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.criticality}
            label="Criticidade"
            value={filters.criticality}
            onChange={set('criticality')}
          >
            <option value="">Todas</option>
            {ALERT_CRITICALITIES.map((c) => (
              <option key={c} value={c}>
                {ALERT_CRITICALITY_LABELS[c]}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.perspective}
            label="Perspectiva"
            value={filters.perspectiveId}
            onChange={set('perspectiveId')}
          >
            <option value="">Todas as perspectivas</option>
            {opts?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.department}
            label="Área"
            value={filters.departmentId}
            onChange={set('departmentId')}
          >
            <option value="">Todas as áreas</option>
            {opts?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.responsible}
            label="Responsável"
            value={filters.responsibleId}
            onChange={set('responsibleId')}
          >
            <option value="">Todos os responsáveis</option>
            {opts?.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Filter>
          <Filter id={ids.kpi} label="Indicador" value={filters.kpiId} onChange={set('kpiId')}>
            <option value="">Todos os indicadores</option>
            {opts?.kpis.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name} ({k.code})
              </option>
            ))}
          </Filter>
          <Filter
            id={ids.unit}
            label="Unidade"
            value={filters.businessUnitId}
            onChange={set('businessUnitId')}
          >
            <option value="">Todas as unidades</option>
            {opts?.businessUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Filter>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.from} className="text-sm font-medium">
              Período: de
            </label>
            <Input
              id={ids.from}
              type="date"
              value={filters.periodFrom}
              onChange={(e) => set('periodFrom')(e.target.value)}
              className="h-11"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={ids.to} className="text-sm font-medium">
              Período: até
            </label>
            <Input
              id={ids.to}
              type="date"
              value={filters.periodTo}
              min={filters.periodFrom || undefined}
              onChange={(e) => set('periodTo')(e.target.value)}
              className="h-11"
            />
          </div>
        </div>
      </div>

      {elsewhere > 0 ? (
        <p className="rounded-lg border border-border bg-muted px-4 py-3 text-sm" role="status">
          {elsewhere === 1
            ? 'Há 1 alerta aberto em outro projeto em andamento: troque o projeto acima para vê-lo.'
            : `Há ${elsewhere} alertas abertos em outros projetos em andamento: troque o projeto acima para vê-los.`}
        </p>
      ) : null}
      {list.isPending ? (
        <LoadingState rows={4} label="Carregando os alertas" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={BellRing}
          title={filtered ? 'Nenhum alerta com estes filtros' : 'Nenhum alerta aberto'}
          description={
            filtered
              ? 'Mude ou limpe os filtros para ver outros alertas.'
              : 'Quando um indicador sai da faixa, uma ação atrasa ou alguém informa um desvio, o alerta aparece aqui.'
          }
        />
      ) : (
        <>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {list.data.total === 1 ? '1 alerta' : `${list.data.total} alertas`}
            {list.isFetching ? ' · atualizando…' : ''}
          </p>
          <ul
            aria-label="Alertas"
            aria-busy={list.isFetching}
            className={
              list.isFetching ? 'flex flex-col gap-3 opacity-70 transition-opacity' : 'flex flex-col gap-3'
            }
          >
            {list.data.items.map((a) => (
              <AlertCard
                key={a.id}
                alert={a}
                organizationId={organizationId}
                canTreat={can('alertas', 'edit')}
                canPlan={can('planos-acao', 'create')}
                busy={take.isPending}
                onTake={() => take.mutate(a)}
                onClose={(status) => setClosing({ alert: a, status })}
              />
            ))}
          </ul>
          {list.data.totalPages > 1 ? (
            <nav aria-label="Paginação de alertas" className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Página {list.data.page} de {list.data.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((x) => x - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= list.data.totalPages}
                  onClick={() => setPage((x) => x + 1)}
                >
                  Próxima
                </Button>
              </div>
            </nav>
          ) : null}
        </>
      )}

      <Dialog
        open={closing !== null}
        onOpenChange={(open) => !open && setClosing(null)}
        busy={busy}
        title={closing?.status === 'DISMISSED' ? 'Descartar alerta' : 'Resolver alerta'}
        description={closing?.alert.title}
      >
        {closing ? (
          <CloseForm
            key={closing.alert.id}
            url={`${base}/${closing.alert.id}/status`}
            status={closing.status}
            onBusyChange={setBusy}
            onDone={async (a) => {
              setClosing(null);
              toast.success(a.status === 'DISMISSED' ? 'Alerta descartado' : 'Alerta resolvido', {
                description: a.title,
              });
              await refresh();
            }}
            onCancel={() => setClosing(null)}
            onConflict={onConflict}
          />
        ) : null}
      </Dialog>
    </section>
  );
}

function AlertCard({
  alert: a,
  organizationId,
  canTreat,
  canPlan,
  busy,
  onTake,
  onClose,
}: {
  alert: Alert;
  organizationId: string;
  canTreat: boolean;
  canPlan: boolean;
  busy: boolean;
  onTake: () => void;
  onClose: (status: 'RESOLVED' | 'DISMISSED') => void;
}) {
  const can = useCan();
  const open = ALERT_OPEN_STATUSES.includes(a.status);
  const closed = ALERT_CLOSED_STATUSES.includes(a.status);
  const ws = (path: string) => `/empresas/${organizationId}/${path}`;
  const reportedBy =
    a.type === 'MANUAL_DEVIATION' && typeof a.details.reportedByName === 'string'
      ? a.details.reportedByName
      : null;
  // Atalho para o assunto, só para quem acessa o módulo de destino.
  const subject: ReactNode = a.kpi ? (
    can('metas-resultados', 'view') ? (
      <Link
        className="inline-flex items-center font-semibold underline-offset-4 hover:underline"
        to={ws(`indicadores/historico?kpi=${a.kpi.id}`)}
      >
        Abrir o indicador {a.kpi.name}
      </Link>
    ) : (
      <span>Indicador: {a.kpi.name}</span>
    )
  ) : a.objective ? (
    can('objetivos', 'view') ? (
      <Link
        className="inline-flex items-center font-semibold underline-offset-4 hover:underline"
        to={ws(`estrategia/objetivos?q=${encodeURIComponent(a.objective.name)}`)}
      >
        Abrir o objetivo {a.objective.name}
      </Link>
    ) : (
      <span>Objetivo: {a.objective.name}</span>
    )
  ) : a.action ? (
    can('planos-acao', 'view') ? (
      <Link
        className="inline-flex items-center font-semibold underline-offset-4 hover:underline"
        to={ws(`execucao/planos-acao?q=${encodeURIComponent(a.action.title)}`)}
      >
        Abrir a ação {a.action.title}
      </Link>
    ) : (
      <span>Ação: {a.action.title}</span>
    )
  ) : null;

  return (
    <li className="rounded-lg border border-border bg-surface p-4">
      <article aria-label={a.title}>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold">{a.title}</h3>
          <StatusBadge
            status={CRITICALITY_LIGHT[a.criticality]}
            label={ALERT_CRITICALITY_LABELS[a.criticality]}
          />
          <StatusBadge status={STATUS_LIGHT[a.status]} label={ALERT_STATUS_LABELS[a.status]} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {ALERT_TYPE_LABELS[a.type]} · Período: {periodText(a)} · Desde {formatDateTime(a.createdAt)}
        </p>
        <p className="mt-2 text-sm">{a.message}</p>
        <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
          <Meta label="Área">
            {[a.department?.name, a.businessUnit?.name].filter(Boolean).join(' · ') || 'Empresa toda'}
          </Meta>
          <Meta label="Responsável">{a.responsible?.name ?? 'Sem responsável'}</Meta>
          <Meta label="Perspectiva">{a.perspective?.name ?? '—'}</Meta>
          {reportedBy ? <Meta label="Informado por">{reportedBy}</Meta> : null}
        </dl>
        {closed ? (
          <p className="mt-2 rounded-md bg-muted px-3 py-2 text-sm">
            {a.autoResolved
              ? `Resolvido automaticamente em ${formatDateTime(a.closedAt!)}. `
              : `${ALERT_STATUS_LABELS[a.status]} por ${a.closedByName} em ${formatDateTime(a.closedAt!)}: `}
            {a.autoResolved ? a.resolution?.replace(/^Resolvido automaticamente: /, '') : a.resolution}
          </p>
        ) : null}
        {a.plans.length ? (
          <p className="mt-2 text-sm">
            <span className="text-muted-foreground">Planos de ação: </span>
            {a.plans.map((p, i) => (
              <span key={p.id}>
                {i > 0 ? ', ' : ''}
                {can('planos-acao', 'view') ? (
                  <Link
                    className="font-medium underline-offset-4 hover:underline"
                    to={ws(`execucao/planos-acao?q=${encodeURIComponent(p.name)}`)}
                  >
                    {p.name}
                  </Link>
                ) : (
                  p.name
                )}
                {p.archived ? ' (arquivado)' : ''}
              </span>
            ))}
          </p>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          {subject}
          <div className="ml-auto flex flex-wrap gap-2">
            {open && canTreat && a.status === 'OPEN' ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={onTake}>
                Assumir
              </Button>
            ) : null}
            {open && canPlan ? (
              <Button asChild size="sm" variant="outline">
                <Link to={ws(`execucao/planos-acao?alerta=${a.id}`)}>Gerar plano de ação</Link>
              </Button>
            ) : null}
            {open && canTreat ? (
              <>
                <Button size="sm" variant="outline" onClick={() => onClose('RESOLVED')}>
                  Resolver
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onClose('DISMISSED')}>
                  Descartar
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </article>
    </li>
  );
}

function CloseForm({
  url,
  status,
  onDone,
  onCancel,
  onBusyChange,
  onConflict,
}: {
  url: string;
  status: 'RESOLVED' | 'DISMISSED';
  onDone: (a: Alert) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
  onConflict: (err: unknown) => void;
}) {
  const { client } = useAuth();
  const [justification, setJustification] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = alertStatusInputSchema.safeParse({ status, justification });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    onBusyChange(true);
    try {
      const saved = await client.request(url, { method: 'POST', body: parsed.data, schema: alertSchema });
      await onDone(saved);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
      onConflict(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <FormField
        id={id}
        label="Justificativa"
        error={errors.justification}
        hint={
          <p className="text-xs text-muted-foreground">
            {status === 'DISMISSED'
              ? 'Por que o alerta não pede ação (fica na trilha).'
              : 'O que foi feito para resolver (fica na trilha).'}
          </p>
        }
      >
        <textarea
          {...fieldA11y(id, errors.justification, true)}
          value={justification}
          rows={4}
          maxLength={ALERT_JUSTIFICATION_MAX}
          onChange={(e) => setJustification(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Voltar
        </Button>
        <Button type="submit" loading={saving}>
          {status === 'DISMISSED' ? 'Descartar alerta' : 'Resolver alerta'}
        </Button>
      </div>
    </form>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-1">
      <dt className="shrink-0 text-muted-foreground">{label}:</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function Filter({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </NativeSelect>
    </div>
  );
}
